import React from 'react';
import { AlertTriangle, Building2, CheckCircle2, MapPin, Package, RefreshCw } from 'lucide-react';

import { formatIST } from '@/lib/utils';
import { INPUT_CLASS, inputStyle } from '@/components/phone/FieldControls';
import {
  BottomSheet, ChipRow, EmptyState, IconButton, PageHeader, Pager, PhonePage, PrimaryButton, ScreenLoader, SearchBar,
  StatusPill, TabRow,
} from '@/components/phone/primitives';
import { Colors, pcs } from '@/components/phone/theme';

/**
 * What is out of the building, on a phone.
 *
 * The desktop is a ten-column grid with a receive strip; a card says what went
 * out, to whom, and how long ago. Destination and status are chip rows rather
 * than a stat strip, and receiving something back opens a sheet at the thumb
 * with the one optional note. The ledger keeps its tab beside the tracker.
 */

const STATUS_TONE = {
  sent: 'accent', in_progress: 'caution', completed: 'positive', sent_back: 'caution', received: 'neutral',
};

function TrackerCard({ item, statusLabels, onReceive }) {
  const late = (item.days_out ?? 0) >= 3 && item.status !== 'received';
  const receivable = item.status === 'sent_back' || item.status === 'completed';
  return (
    <li
      data-testid={`tracker-card-${item.id}`}
      className="mt-3 rounded-[10px] border"
      style={{
        backgroundColor: Colors.background.primary,
        borderColor: item.color === 'red' ? Colors.status.error : item.color === 'green' ? Colors.semantic.positive : Colors.border.default,
      }}
    >
      <div className="px-4 pt-3">
        <p className="line-clamp-2 text-[15px] font-bold -tracking-[0.2px]" style={{ color: Colors.text.primary }}>
          {item.item_name || item.product_subtype || item.order_type || 'Item'}
        </p>
        <p className="mt-0.5 truncate text-[12px] font-medium" style={{ color: Colors.text.secondary }}>
          {item.customer_name}{item.firm_name ? ` — ${item.firm_name}` : ''}
        </p>
        <p className="mt-0.5 truncate text-[11px]" style={{ color: Colors.text.tertiary }}>
          {item.order_display_id} · {item.stage_name}{item.quantity ? ` · ${pcs(item.quantity)}` : ''}
        </p>
        <p className="mt-1.5 flex items-center gap-1.5 text-[12px]" style={{ color: Colors.text.secondary }}>
          {item.destination_type === 'city_office'
            ? <Building2 className="h-3.5 w-3.5 shrink-0" style={{ color: Colors.accent.DEFAULT }} />
            : <MapPin className="h-3.5 w-3.5 shrink-0" style={{ color: Colors.semantic.caution }} />}
          <span className="truncate">{item.destination_name}</span>
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 px-4 pb-3 pt-2">
        <StatusPill label={statusLabels[item.status] || item.status} tone={STATUS_TONE[item.status] || 'neutral'} dot />
        <span
          className="flex items-center gap-1 text-[12px] font-semibold"
          style={{ color: late ? Colors.status.error : Colors.text.secondary }}
        >
          {late && <AlertTriangle className="h-3.5 w-3.5" />}
          {item.days_out ?? 0}d out
        </span>
        {!!item.sent_at && (
          <span className="text-[11px]" style={{ color: Colors.text.tertiary }}>
            sent {formatIST(item.sent_at, { dateOnly: true })}
          </span>
        )}
      </div>

      {receivable && (
        <div className="border-t px-3 py-2" style={{ borderColor: Colors.border.subtle }}>
          <button
            type="button"
            data-testid={`receive-btn-${item.id}`}
            onClick={onReceive}
            className="flex h-11 w-full items-center justify-center gap-1.5 rounded-[10px] text-[13px] font-bold"
            style={{ backgroundColor: Colors.semantic.positive, color: Colors.text.inverse }}
          >
            <CheckCircle2 className="h-4 w-4" /> Receive back
          </button>
        </div>
      )}
    </li>
  );
}

export default function PhoneTracker({
  view, onView, items, total, loading, onSearch, onRefresh,
  destinations, destination, onDestination, statusLabels, status, onStatus, counts,
  page, totalPages, onPage,
  receiving, notes, onNotes, onReceive, onCancelReceive, onConfirmReceive, submitting,
  ledger,
}) {
  const receivingItem = items.find((i) => i.id === receiving);
  return (
    <PhonePage testID="external-processing-page">
      <PageHeader
        title="External Processing"
        subtitle={view === 'tracker'
          ? `${total} item${total === 1 ? '' : 's'} out${counts.overdue ? ` · ${counts.overdue} over 3 days` : ''}`
          : 'contractor charges and reconciliation'}
        action={<IconButton icon={RefreshCw} onClick={onRefresh} label="Refresh" disabled={loading} />}
      />

      <div className="px-5">
        <TabRow
          tabs={[
            { key: 'tracker', label: 'Tracker', testID: 'view-tracker' },
            { key: 'ledger', label: 'Job Work Ledger', testID: 'view-ledger' },
          ]}
          active={view}
          onSelect={onView}
        />
      </div>

      {view === 'ledger' ? (
        <div className="flex-1 px-5 pb-8 pt-3">{ledger}</div>
      ) : (
        <>
          <div className="mt-3 px-5">
            <SearchBar placeholder="Order ID, customer, vendor…" onChange={onSearch} testID="search-input" />
            <div className="mt-2">
              <ChipRow
                chips={[
                  { key: null, label: 'Anywhere' },
                  ...destinations.map(([key, label]) => ({ key, label: `${label} ${counts[key] ?? 0}` })),
                ]}
                active={destination}
                onSelect={onDestination}
                testIDPrefix="filter-destination"
              />
            </div>
            <div className="mt-2">
              <ChipRow
                chips={[
                  { key: null, label: 'Any status' },
                  ...Object.entries(statusLabels).map(([key, label]) => ({ key, label })),
                ]}
                active={status}
                onSelect={onStatus}
                testIDPrefix="filter-status"
              />
            </div>
          </div>

          <div className="flex-1 px-5 pb-8">
            {loading ? (
              <ScreenLoader />
            ) : items.length === 0 ? (
              <EmptyState icon={Package} message="No items in external processing" />
            ) : (
              <ul data-testid="tracker-list">
                {items.map((item) => (
                  <TrackerCard key={item.id} item={item} statusLabels={statusLabels} onReceive={() => onReceive(item)} />
                ))}
              </ul>
            )}
            <Pager page={page} totalPages={totalPages} onPage={onPage} />
          </div>
        </>
      )}

      <BottomSheet
        open={!!receivingItem}
        onClose={onCancelReceive}
        title={receivingItem ? `Receive back from ${receivingItem.destination_name}` : ''}
        subtitle={receivingItem ? `${receivingItem.order_display_id} · ${receivingItem.stage_name} · ${pcs(receivingItem.quantity)}` : ''}
        testID="receive-sheet"
      >
        <label className="block">
          <span className="mb-1.5 block text-[11px] font-medium" style={{ color: Colors.text.secondary }}>Notes</span>
          <input
            data-testid="receive-notes"
            className={INPUT_CLASS}
            style={inputStyle}
            value={notes}
            onChange={(e) => onNotes(e.target.value)}
            placeholder="Optional — shortfall, damage, who handed it over"
            aria-label="Receive notes"
          />
        </label>
        <p className="mt-3 text-[12px]" style={{ color: Colors.text.secondary }}>
          It moves to the next production stage.
        </p>
        <PrimaryButton className="mt-4" onClick={onConfirmReceive} busy={submitting} testID="confirm-receive-btn">
          Received
        </PrimaryButton>
      </BottomSheet>
    </PhonePage>
  );
}
